/**
 * Data for the FOMO social layer (docs/API.md "Social"): the live activity tape (one shared SSE connection per tab),
 * batched post reactions, recent token trades and web push. Every response passes the fail-closed
 * validation in lib/api/client.ts; SSE frames are parsed with the route's `ActivityItem` schema.
 */
import { useSyncExternalStore } from 'react'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { API_BASE, ActivityItem, type PostReactions, type ReactionKind } from '@algoboo/schema/api'
import { createSseParser, request, type SseFrame } from '../../lib/api'
import { isIos } from '../../pwa/install'
import { base64UrlToBytes } from '../../pwa/keys'
import { isStandalone } from '../../pwa/register'
import { disablePushWith, enablePushWith, pushCapability, syncPushWith, type PushBrowser, type PushCapability, type PushServer, type ServerKey } from '../../pwa/subscription'

// Query keys ---------------------------------------------------------------------------------

export const socialKeys = {
  tokenTrades: (address: string) => ['tokens', 'trades', address.toLowerCase()] as const,
  /** Under `me`: the caller's own reactions change with the session, so sign-in/out drops them. */
  reactions: (postId: string) => ['me', 'reactions', postId] as const,
  vapid: () => ['catalog', 'vapid-key'] as const,
}

// Activity tape ------------------------------------------------------------------------------

export type ActivityStatus = 'connecting' | 'live' | 'reconnecting' | 'off'

export interface ActivitySnapshot {
  items: readonly ActivityItem[]
  status: ActivityStatus
  /** ids that arrived over the stream in the last few seconds (for the slide-in) */
  fresh: ReadonlySet<string>
}

const KEEP = 40
const FRESH_MS = 6_000
const SEED_LIMIT = 20

/** Order of tape ids (decimal sequence numbers). */
const newer = (a: string, b: string) => (a.length !== b.length ? a.length > b.length : a > b)

/**
 * The tape store: seeded from `GET /activity`, then live over `GET /activity/stream` (fetch streaming, so
 * `Last-Event-ID` is sent on reconnect and the server replays what was missed). Connected while at least one
 * component subscribes; shared by the desktop ticker and the mobile live row.
 */
class ActivityStore {
  private snap: ActivitySnapshot = { items: [], status: 'off', fresh: new Set() }
  private readonly listeners = new Set<() => void>()
  private controller: AbortController | null = null
  private timer: ReturnType<typeof setTimeout> | undefined
  private prune: ReturnType<typeof setInterval> | undefined
  private readonly freshAt = new Map<string, number>()
  private lastId: string | null = null

  get = (): ActivitySnapshot => this.snap

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn)
    if (this.listeners.size === 1) this.start()
    return () => {
      this.listeners.delete(fn)
      if (this.listeners.size === 0) this.stop()
    }
  }

  private set(next: Partial<ActivitySnapshot>): void {
    this.snap = { ...this.snap, ...next }
    for (const fn of this.listeners) fn()
  }

  /** Merge items (any order), newest first, deduped by id; `live` marks them fresh. */
  merge(incoming: readonly ActivityItem[], live: boolean): void {
    const byId = new Map(this.snap.items.map((i) => [i.id, i]))
    let added = false
    for (const it of incoming) {
      if (byId.has(it.id)) continue
      byId.set(it.id, it)
      added = true
      if (live) this.freshAt.set(it.id, Date.now())
      if (!this.lastId || newer(it.id, this.lastId)) this.lastId = it.id
    }
    if (!added) return
    const items = [...byId.values()].sort((a, b) => (newer(a.id, b.id) ? -1 : newer(b.id, a.id) ? 1 : 0)).slice(0, KEEP)
    this.set({ items, fresh: new Set(this.freshAt.keys()) })
  }

  private start(): void {
    const controller = new AbortController()
    this.controller = controller
    this.set({ status: 'connecting' })
    this.prune = setInterval(() => {
      const now = Date.now()
      let changed = false
      for (const [id, t] of this.freshAt) if (now - t > FRESH_MS) changed = this.freshAt.delete(id) || changed
      if (changed) this.set({ fresh: new Set(this.freshAt.keys()) })
    }, 2_000)
    let attempt = 0
    const onFrame = (f: SseFrame) => {
      if (f.id) this.lastId = f.id
      if (f.event !== 'activity' && f.event !== 'message') return
      let json: unknown
      try {
        json = JSON.parse(f.data)
      } catch {
        return
      }
      const parsed = ActivityItem.safeParse(json)
      if (parsed.success) this.merge([parsed.data], true)
      else console.warn('[activity] dropped invalid item', parsed.error.issues)
    }
    const connect = async () => {
      this.set({ status: attempt === 0 ? 'connecting' : 'reconnecting' })
      try {
        if (!this.lastId) {
          // Seed (newest first); the stream then resumes after the newest seeded id.
          const page = await request('listActivity', { query: { limit: SEED_LIMIT }, signal: controller.signal })
          this.merge(page.items, false)
        }
        const headers: Record<string, string> = { Accept: 'text/event-stream' }
        if (this.lastId) headers['Last-Event-ID'] = this.lastId
        const res = await globalThis.fetch(`${API_BASE}/activity/stream`, { headers, credentials: 'include', signal: controller.signal })
        if (!res.ok || !res.body) throw new Error(`activity stream http ${res.status}`)
        attempt = 0
        this.set({ status: 'live' })
        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        const parse = createSseParser()
        for (;;) {
          const { value, done } = await reader.read()
          if (done) break
          for (const f of parse(decoder.decode(value, { stream: true }))) onFrame(f)
        }
        throw new Error('activity stream closed')
      } catch {
        if (controller.signal.aborted) return
        attempt++
        this.set({ status: 'reconnecting' })
        this.timer = setTimeout(() => void connect(), Math.min(30_000, 1_000 * 2 ** Math.min(attempt - 1, 5)))
      }
    }
    void connect()
  }

  private stop(): void {
    this.controller?.abort()
    this.controller = null
    if (this.timer) clearTimeout(this.timer)
    if (this.prune) clearInterval(this.prune)
    this.freshAt.clear()
    this.snap = { ...this.snap, status: 'off', fresh: new Set() }
  }

  /** Tests: forget everything (the next subscriber seeds again). */
  reset(): void {
    this.stop()
    this.lastId = null
    this.snap = { items: [], status: 'off', fresh: new Set() }
  }
}

export const activityStore = new ActivityStore()

/** The live tape, newest first. Mounting any consumer opens the shared stream. */
export function useActivity(): ActivitySnapshot {
  return useSyncExternalStore(activityStore.subscribe, activityStore.get, activityStore.get)
}

// Reactions ----------------------------------------------------------------------------------

export const REACTIONS: readonly { kind: ReactionKind; glyph: string; label: string }[] = [
  { kind: 'fire', glyph: '🔥', label: 'Fire' },
  { kind: 'eyes', glyph: '👀', label: 'Eyes' },
  { kind: 'rocket', glyph: '🚀', label: 'Rocket' },
  { kind: 'skull', glyph: '💀', label: 'Skull' },
]

const EMPTY_COUNTS = { fire: 0, eyes: 0, skull: 0, rocket: 0 } as const
export const emptyReactions = (postId: string): PostReactions => ({ postId, counts: { ...EMPTY_COUNTS }, mine: [] })

/**
 * Batches reaction reads: every post that asks within one tick is fetched with one
 * `GET /posts/reactions?ids=` (chunks of 100), so a loaded feed page costs one request.
 */
function createReactionLoader() {
  let pending = new Map<string, { resolve: (r: PostReactions) => void; reject: (e: unknown) => void }[]>()
  let scheduled = false
  const flush = async () => {
    scheduled = false
    const batch = pending
    pending = new Map()
    const ids = [...batch.keys()]
    for (let i = 0; i < ids.length; i += 100) {
      const chunk = ids.slice(i, i + 100)
      try {
        const rows = await request('getPostReactions', { query: { ids: chunk.join(',') } })
        const byId = new Map(rows.map((r) => [r.postId, r]))
        for (const id of chunk) for (const w of batch.get(id) ?? []) w.resolve(byId.get(id) ?? emptyReactions(id))
      } catch (e) {
        for (const id of chunk) for (const w of batch.get(id) ?? []) w.reject(e)
      }
    }
  }
  return (postId: string) =>
    new Promise<PostReactions>((resolve, reject) => {
      const list = pending.get(postId) ?? []
      list.push({ resolve, reject })
      pending.set(postId, list)
      if (!scheduled) {
        scheduled = true
        setTimeout(() => void flush(), 0)
      }
    })
}

const loadReactions = createReactionLoader()

export function usePostReactions(postId: string) {
  return useQuery({ queryKey: socialKeys.reactions(postId), queryFn: () => loadReactions(postId), staleTime: 30_000 })
}

/** The counts after the caller toggles `kind` (optimistic; the server's answer replaces it). */
export function toggled(r: PostReactions, kind: ReactionKind, on: boolean): PostReactions {
  const had = r.mine.includes(kind)
  if (had === on) return r
  const counts = { ...r.counts, [kind]: Math.max(0, r.counts[kind] + (on ? 1 : -1)) }
  return { ...r, counts, mine: on ? [...r.mine, kind] : r.mine.filter((k) => k !== kind) }
}

export function useToggleReaction(postId: string) {
  const qc = useQueryClient()
  const key = socialKeys.reactions(postId)
  return useMutation<PostReactions, Error, { kind: ReactionKind; on: boolean }, { prev: PostReactions | undefined }>({
    mutationKey: ['reaction', postId],
    mutationFn: ({ kind, on }) => (on ? request('addPostReaction', { params: { id: postId }, body: { kind } }) : request('removePostReaction', { params: { id: postId }, body: { kind } })),
    onMutate: async ({ kind, on }) => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<PostReactions>(key)
      qc.setQueryData<PostReactions>(key, toggled(prev ?? emptyReactions(postId), kind, on))
      return { prev }
    },
    // Rate limited (429) or any other refusal: undo only this toggle. Restoring the snapshot taken before it would also
    // wipe a later toggle that the server accepted meanwhile (lane V-R4).
    onError: (_e, { kind, on }, c) => {
      const cur = qc.getQueryData<PostReactions>(key)
      qc.setQueryData<PostReactions | undefined>(key, cur ? toggled(cur, kind, !on) : c?.prev)
    },
    onSuccess: (data) => qc.setQueryData(key, data),
    // The last toggle in flight for this post settles on the server's own answer (responses may arrive out of order).
    onSettled: async () => {
      if (qc.isMutating({ mutationKey: ['reaction', postId] }) <= 1) await qc.invalidateQueries({ queryKey: key })
    },
  })
}

// Token trades -------------------------------------------------------------------------------

export const tokenTradesQuery = (address: string) =>
  queryOptions({
    queryKey: socialKeys.tokenTrades(address),
    queryFn: ({ signal }) => request('getTokenTrades', { signal, params: { address: address.toLowerCase() as `0x${string}` }, query: { limit: 20 } }),
    refetchInterval: 15_000,
  })

export function useTokenTrades(address: string, enabled = true) {
  return useQuery({ ...tokenTradesQuery(address), enabled: enabled && /^0x[0-9a-fA-F]{40}$/.test(address) })
}

// Web push (docs/PWA.md "Push lifecycle"; the lifecycle itself is src/pwa/subscription.ts) -------------------------

export function pushSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/** 'ok', 'ios-install' (iOS Safari tab: push works only in the home-screen app) or 'unsupported'. */
export function currentPushCapability(): PushCapability {
  if (typeof window === 'undefined') return 'unsupported'
  return pushCapability({
    ios: isIos(navigator.userAgent, navigator.maxTouchPoints ?? 0),
    standalone: isStandalone(),
    hasPushManager: 'PushManager' in window,
    hasServiceWorker: 'serviceWorker' in navigator,
    hasNotification: 'Notification' in window,
  })
}

export const vapidKeyQuery = () => queryOptions({ queryKey: socialKeys.vapid(), queryFn: ({ signal }) => request('getVapidKey', { signal }), staleTime: Infinity })

export function useVapidKey(enabled = true) {
  return useQuery({ ...vapidKeyQuery(), enabled })
}

export { base64UrlToBytes }

/** The service worker registration, or null (development and tests register none, see pwa/register.ts). */
export async function swRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null
  try {
    return (await navigator.serviceWorker.getRegistration()) ?? null
  } catch {
    return null
  }
}

function browserPush(reg: ServiceWorkerRegistration): PushBrowser {
  return {
    getSubscription: () => reg.pushManager.getSubscription(),
    subscribe: (publicKey) => reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlToBytes(publicKey) }),
    permission: () => Notification.permission,
    requestPermission: () => Notification.requestPermission(),
    store: {
      get: (k) => {
        try {
          return localStorage.getItem(k)
        } catch {
          return null
        }
      },
      set: (k, v) => {
        try {
          localStorage.setItem(k, v)
        } catch {
          // storage blocked: the key comparison falls back to the subscription's own key
        }
      },
      remove: (k) => {
        try {
          localStorage.removeItem(k)
        } catch {
          // storage blocked
        }
      },
    },
  }
}

const pushServer: PushServer = {
  register: async (sub) => {
    await request('createPushSubscription', { body: sub })
  },
  unregister: async (endpoint) => {
    await request('deletePushSubscription', { body: { endpoint } })
  },
}

/** Subscribe this browser and register it with the API (call from a click); returns the endpoint. */
export async function enablePush(key: ServerKey): Promise<string> {
  const reg = await swRegistration()
  if (!reg) throw new Error('Push needs the installed app (service worker) on this device.')
  return enablePushWith(browserPush(reg), pushServer, key)
}

/** App start while signed in: keep an existing subscription current (a rotated VAPID key, a new account). */
export async function syncPush(key: ServerKey): Promise<'off' | 'ok' | 'rotated'> {
  const reg = await swRegistration()
  if (!reg) return 'off'
  return syncPushWith(browserPush(reg), pushServer, key)
}

export async function disablePush(): Promise<void> {
  const reg = await swRegistration()
  if (!reg) return
  await disablePushWith(browserPush(reg), pushServer)
}

export async function currentPushEndpoint(): Promise<string | null> {
  const reg = await swRegistration()
  return (await reg?.pushManager.getSubscription())?.endpoint ?? null
}
